<template>
  <p v-if="warning" class="timer-pill" role="timer" aria-live="off">
    زمان باقی‌مانده: <b>{{ formatted }}</b>
  </p>
</template>

<script>
import { toFa } from "@/utils/digits";

/**
 * Session countdown, driven by the JWT `exp`.
 *
 * Owns its own interval and clears it in `beforeDestroy`. The previous implementation ran
 * this from a computed property, which re-armed a fresh `setInterval` on every render and
 * committed a mutation from a getter — this is the same behaviour without the leak.
 *
 * The clock is kept but only *shown* near the end. The redesign dropped the permanent
 * countdown on purpose — a timer ticking down while you photograph your own face reads as
 * pressure, and this is not a timed test. What the user genuinely needs is a warning
 * before the session lapses, so the pill appears in the final `warnAt` seconds and the
 * expiry itself is always handled whether or not anything was on screen.
 *
 * `aria-live="off"` is deliberate: a per-second announcement would make the screen
 * unusable with a reader. The expiry is what matters, and that navigates.
 */
export default {
  name: "CountdownTimer",

  props: {
    /** Epoch seconds. 0 disables the timer. */
    expiresAt: { type: Number, default: 0 },
    /** Show the pill once this many seconds remain. */
    warnAt: { type: Number, default: 120 },
  },

  data() {
    return { remaining: 0 };
  },

  computed: {
    warning() {
      return this.remaining > 0 && this.remaining <= this.warnAt;
    },
    formatted() {
      const total = Math.max(0, this.remaining);
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      const pad = (n) => String(n).padStart(2, "0");
      return toFa(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`);
    },
  },

  watch: {
    expiresAt: {
      immediate: true,
      handler() {
        this.restart();
      },
    },
  },

  beforeDestroy() {
    this.stop();
  },

  methods: {
    restart() {
      this.stop();
      if (!this.expiresAt) {
        this.remaining = 0;
        return;
      }
      this.update();
      this.tick = setInterval(this.update, 1000);
    },

    update() {
      this.remaining = Math.max(0, Math.floor(this.expiresAt - Date.now() / 1000));
      if (this.remaining <= 0) {
        this.stop();
        this.$emit("expired");
      }
    },

    stop() {
      if (this.tick) {
        clearInterval(this.tick);
        this.tick = null;
      }
    },
  },
};
</script>

<style scoped>
.timer-pill {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 45;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--warn-soft);
  color: var(--warn);
  border: 1px solid var(--warn);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 700;
}

.timer-pill b {
  font-variant-numeric: tabular-nums;
  direction: ltr;
  display: inline-block;
}
</style>
